import axios from "axios";
import merge from "lodash/merge";
import { nanoid } from 'nanoid';
import {stringify} from "./utils";
import errorToJSON from "error-to-json";

interface LogLog {
    level: string;
}

interface LogUser {
    id: number;
    name: string;
    roles: string[];
}

interface LogEvent {
    id: string;
    dataset: string;
    kind?: string;
    action?: string;
    code?: string;
    duration?: number;
}

interface LogRoom {
    id: number;
    online?: number;
}

interface LogItem {
    "@timestamp": string;
    event: LogEvent;
    room?: LogRoom;
    user?: LogUser;
    log?: LogLog;
    message?: string;
    tags?: [string];
    labels?: unknown;
}

interface LogInitOptions {
    uploadUrl: string;
    uploadInterval?: number;
}

interface MetricFields {
    [key: string]: number | string | boolean | undefined;
}

const logStore: LogItem[] = [];
let logUser: LogUser;
let logRoom: LogRoom;
const logOptions: Partial<LogInitOptions> = {};
let logUploadLevels: string[] = ["DEBUG", "INFO", "WARN", "ERROR"];
const logIdPrefix = nanoid(8) + "_";
let logId = 1;
let uploadTimer: ReturnType<typeof setInterval> | undefined;
let uploading = false;

// eslint-disable-next-line
function _( level: string, message: string, context?: any): string {
    let text = new Date().toLocaleTimeString() + ' EduLive [' + level.toUpperCase() + ']: ' + message;

    if (context) {
        text += stringify(context);
    }

    return text;
}

function t(timestamp?: number): string {
    if (!timestamp) {
        timestamp = new Date().getTime();
    }

    const date = new Date();
    date.setTime(timestamp);

    return date.toISOString();
}

function insertStore(item: LogItem) {
    if (logStore.length > 500) {
        return ;
    }

    logStore.push(item);
}

function pushLog(level: string, message: string, context: any = undefined) {
    if (context) {
        message += " " + stringify(context);
    }

    const item = buildLogItem();
    item.log = {level: level};
    item.message = message;
    insertStore(item);
}

function buildLogItem(): LogItem {
    const item = {"@timestamp": t(), event: {dataset: "livecloud.client", id: logIdPrefix + logId}} as LogItem;
    logId ++;

    if (logUser) {
        item.user = logUser;
    }

    if (logRoom) {
        item.room = logRoom;
    }

    return item;
}

function popLogs(count = 100): LogItem[] {
    const poppedLogs: LogItem[] = [];
    const logs = logStore.splice(0, count);
    for (const item of logs) {
        if (item.log?.level && logUploadLevels.indexOf(item.log.level.toUpperCase()) < 0) {
            continue;
        }

        poppedLogs.push(item);
    }

    return poppedLogs;
}

export default class Logger {

    static boot(options: LogInitOptions):void {
        logOptions.uploadUrl = options.uploadUrl;
        logOptions.uploadInterval = options.uploadInterval ? options.uploadInterval : 10000;

        if (!logOptions.uploadUrl || logOptions.uploadUrl.length === 0) {
            return ;
        }

        uploadTimer = setInterval(async () => {
            if (uploading) {
                return ;
            }

            const body = {
                "@timestamp": t(),
                logs: popLogs(100),
            };

            if (body.logs.length === 0) {
                return;
            }

            try {
                uploading = true;
                await axios.post(logOptions.uploadUrl as string, body, {
                    timeout: 6000,
                });
            } catch (err) {
                this.event("Logger.PushError", (err as Error).message ? (err as Error).message : "",);
            }
            uploading = false;

        }, logOptions.uploadInterval);
    }

    static shutdown() {
        if (uploadTimer) {
            clearInterval(uploadTimer)
        }
    }

    static setUser(user: LogUser): Logger {
        logUser = user;
        return self;
    }

    static setRoom(room: LogRoom): Logger {
        logRoom = room;
        return self;
    }

    static setUploadLevels(levels: string[]): void {
        logUploadLevels = levels;
    }

    // eslint-disable-next-line
    static debug(message: string, context: any = undefined): void {
        console.debug(_('DEBUG', message, context));
        pushLog("DEBUG", message, context);
    }

    // eslint-disable-next-line
    static info(message: string, context: any = undefined): void {
        console.info(_('INFO', message, context));
        pushLog("INFO", message, context);
    }

    // eslint-disable-next-line
    static warn(message: string, context: any = undefined): void {
        console.warn(_('WARN', message, context));
        pushLog("WARN", message, context);
    }

    // eslint-disable-next-line
    static error(message: string, context: any = undefined): void {
        console.error(_('ERROR', message, context));
        pushLog("ERROR", message, context);
    }

    static event(name: string, message?: unknown, duration?: number): void {
        let messageStr: string;
        if (typeof message !== 'string') {
            messageStr = stringify(message);
        } else {
            messageStr = message;
        }

        const text = `@(${name})` + (message ? ', ' + messageStr : '') + (duration ? ', ' + duration : '');
        console.debug(_('DEBUG', text));

        const item = buildLogItem();
        item.event.kind = "event";
        item.event.action = name;
        if (duration) {
            item.event.duration = duration;
        }

        item.message = text;

        const isError = name.substring(name.length - 5) === 'Error';
        item.log = {level: isError ? 'ERROR' : 'INFO'}

        insertStore(item);
    }

    /**
     * 记录 Debug 级别的事件日志
     *
     * @param event 事件名
     * @param message 日志内容
     * @param context 本条事件日志的上下文
     * @param userOriented 终端用户是否可见，请使用用户可理解的语言描述日志内容
     * @param reportOnceWithinSeconds 在指定时间内，同样的 context 只上报一次
     */
    static debug2(event: string, message: string, context?: Record<string, unknown>, userOriented = false, reportOnceWithinSeconds = 0) {
        Logger.event2('DEBUG', event, message, context, userOriented, reportOnceWithinSeconds);
    }

    /**
     * 记录 Info 级别的事件日志
     *
     * @param event 事件名
     * @param message 日志内容
     * @param context 本条事件日志的上下文
     * @param userOriented 终端用户是否可见，请使用用户可理解的语言描述日志内容
     * @param reportOnceWithinSeconds 在指定时间内，同样的 context 只上报一次
     *
     */
    static info2(event: string, message: string, context?: unknown, userOriented = false, reportOnceWithinSeconds = 0) {
        Logger.event2('INFO', event, message, context, userOriented, reportOnceWithinSeconds);
    }

    /**
     * 记录 Warn 级别的事件日志
     *
     * @param event 事件名
     * @param message 日志内容
     * @param context 本条事件日志的上下文
     * @param userOriented 终端用户是否可见，请使用用户可理解的语言描述日志内容
     * @param reportOnceWithinSeconds 在指定时间内，同样的 context 只上报一次
     */
    static warn2(event: string, message: string, context?: Record<string, unknown>, userOriented = false, reportOnceWithinSeconds = 0) {
        Logger.event2('WARN', event, message, context, userOriented, reportOnceWithinSeconds);
    }

    /**
     * 记录 Error 级别的事件日志
     *
     * @param event 事件名
     * @param message 日志内容
     * @param context 本条事件日志的上下文
     * @param userOriented 终端用户是否可见，请使用用户可理解的语言描述日志内容
     * @param reportOnceWithinSeconds 在指定时间内，同样的 context 只上报一次
     */
    static error2(event: string, message: string, context?: Record<string, unknown>, userOriented = false, reportOnceWithinSeconds = 0) {
        Logger.event2('ERROR', event, message, context, userOriented, reportOnceWithinSeconds);
    }

    static event2(level: string, event: string | null, message: string, context?: unknown, userOriented = false, reportOnceWithinSeconds = 0): void {
        if (reportOnceWithinSeconds > 0 && context) {
            const storageTimeKey =  `reportOnceWithinSeconds.${event}.lastTime`;
            const storageContextKey = `reportOnceWithinSeconds.${event}.context`;
            const contextStr = JSON.stringify(context);

            const now = new Date().getTime();
            const lastTime = localStorage.getItem(storageTimeKey) ? parseInt(localStorage.getItem(storageTimeKey) as string) : 0;

            if (contextStr === localStorage.getItem(storageContextKey) && (now - lastTime) < reportOnceWithinSeconds * 1000) {
                return ;
            }

            localStorage.setItem(storageTimeKey, ""+now);
            localStorage.setItem(storageContextKey, contextStr);
        }

        const item = buildLogItem();

        if (event) {
            item.event.kind = "event";
            item.event.action = event;
        }

        item.message = message;
        item.log = {level: level}
        if (userOriented) {
            item.tags = ["userOriented"];
        }

        if (context) {
            if (context instanceof Error) {
                const error = errorToJSON(context) as {stack : string | undefined};
                if (error.stack) {
                    delete error.stack;
                }
                item.labels = error;
            } else {
                item.labels = context;
            }
        }

        insertStore(item);

        if (level === 'DEBUG') {
            console.debug(message, context ? context : '');
        } else if (level === 'INFO') {
            console.info(message, context ? context : '');
        } else if (level === 'WARN') {
            console.warn(message, context ? context : '');
        } else if (level === 'ERROR') {
            console.error(message, context ? context : '');
        } else {
            console.log(message, context ? context : '');
        }
    }
}
